import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { TicketTypeSelector } from './TicketTypeSelector';
import { AttachmentUploader } from './AttachmentUploader';
import { useCreateTicket } from '@/hooks/useCreateTicket';
import { useAttachmentDelete } from '@/hooks/useAttachmentDelete';
import { toast } from 'sonner';

interface CreateTicketModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (ticketId: string) => void;
}

const placeholders = {
  request: 'Descreva o que você precisa de ajuda...',
  suggestion: 'Compartilhe sua ideia de melhoria...',
  compliment: 'Conte o que você gostou...',
  complaint: 'Descreva o problema que você encontrou...',
  report: 'Descreva a situação que você precisa denunciar...',
};

export const CreateTicketModal = ({ open, onOpenChange, onSuccess }: CreateTicketModalProps) => {
  const [type, setType] = useState<'request' | 'suggestion' | 'compliment' | 'complaint' | 'report'>('request');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<string[]>([]);
  
  const createTicket = useCreateTicket();
  const deleteAttachment = useAttachmentDelete();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações com feedback claro para o usuário
    if (!subject.trim()) {
      toast.error('Preencha o assunto do ticket');
      return;
    }

    if (subject.length < 5) {
      toast.error('O assunto deve ter pelo menos 5 caracteres');
      return;
    }

    if (!description.trim()) {
      toast.error('Preencha a descrição do ticket');
      return;
    }

    if (description.length < 20) {
      toast.error('A descrição deve ter pelo menos 20 caracteres');
      return;
    }

    try {
      const ticket = await createTicket.mutateAsync({
        type,
        subject: subject.trim(),
        description: description.trim(),
        attachments,
      });

      // Toast de sucesso
      toast.success('Ticket criado com sucesso!', {
        description: 'Nossa equipe irá analisar e responder em breve.'
      });

      // Reset form
      setType('request');
      setSubject('');
      setDescription('');
      setAttachments([]);
      onOpenChange(false);
      
      if (onSuccess && ticket) {
        onSuccess(ticket.id);
      }
    } catch (error) {
      console.error('Erro ao criar ticket:', error);
      toast.error('Erro ao criar ticket', {
        description: 'Tente novamente em alguns instantes.'
      });
    }
  };

  const handleAttachmentRemove = async (path: string) => {
    await deleteAttachment.mutateAsync(path);
    setAttachments(prev => prev.filter(p => p !== path));
  };

  const isSubmitting = createTicket.isPending;
  const canSubmit = type && subject.trim().length >= 5 && description.trim().length >= 20;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-black/95 backdrop-blur-xl border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white">Criar Ticket de Suporte</DialogTitle>
          <DialogDescription className="text-white/70">
            Descreva seu problema ou sugestão detalhadamente para nossa equipe
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Tipo de Ticket */}
          <div className="space-y-2">
            <Label className="text-white">Tipo de Ticket *</Label>
            <TicketTypeSelector value={type} onChange={setType} />
          </div>

          {/* Assunto */}
          <div className="space-y-2">
            <Label htmlFor="subject" className="text-white">Assunto *</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex: Não consigo enviar mensagens"
              maxLength={100}
              disabled={isSubmitting}
              className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
            />
            <p className="text-xs text-white/40">
              {subject.length}/100 caracteres
            </p>
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-white">Descrição *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={placeholders[type]}
              className="min-h-[120px] resize-none bg-white/5 border-white/20 text-white placeholder:text-white/40"
              maxLength={1000}
              disabled={isSubmitting}
            />
            <p className="text-xs text-white/40">
              {description.length}/1000 caracteres (mínimo 20)
            </p>
          </div>

          {/* Anexos */}
          <div className="space-y-2">
            <Label className="text-white">Anexos (Opcional)</Label>
            {type === 'report' && (
              <p className="text-xs text-yellow-400 mb-2">
                💡 Para denúncias, anexar prints pode ajudar muito na análise
              </p>
            )}
            <AttachmentUploader
              attachments={attachments}
              onUploadComplete={(path) => setAttachments(prev => [...prev, path])}
              onRemove={handleAttachmentRemove}
              maxFiles={5}
            />
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="flex-1 bg-white/5 border-white/20 text-white hover:bg-white/10"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="flex-1 bg-luxury-red hover:bg-luxury-red/90"
            >
              {isSubmitting ? 'Criando...' : 'Criar Ticket'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
